import { useCallback, useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { ErreurApi } from '../../api/client.js';
import { PHASES, PREMIER_CONTACT } from '../../api/contrat.js';
import { Lien, useNaviguer } from '../../app/navigation.js';
import { Pastille } from '../../app/pastille.js';
import { CIBLE_MIN, COULEURS, POLICE_EQUIPE, styleBouton, styleBoutonPrincipal } from '../../app/styles.js';
import { presenterBlocage } from './blocages.js';
import { libelleStatutDossier, REFERENTIEL_LISTE } from './referentiel-liste.js';
import type { LigneDossier, SourceDossiers } from './source-liste.js';

/**
 * Liste des dossiers (équipe), à l'image de la démo : un tableau (une carte par dossier sur mobile), un filtre par texte et
 * par statut, « Nouveau dossier » et son formulaire. Lecture par `dossiers.lister`, création par `dossiers.creer` ;
 * toute règle (droits, mandat signé avant toute recherche d'héritier) reste celle du serveur.
 */
const LARGEUR_TABLEAU = 900;
const MESSAGE_NOM_VIDE = 'Donnez un nom au dossier pour le créer.';
const MESSAGE_ECHEC_CREATION = 'Le dossier n’a pas pu être créé. Vérifiez votre connexion, puis réessayez.';
const MESSAGE_ECHEC_LECTURE = 'La liste des dossiers n’a pas pu être chargée. Réessayez dans un instant.';

const messageDe = (e: unknown, repli: string): string => (e instanceof ErreurApi ? e.messageUtilisateur : repli);

const libellePhase = (code: string): string => {
  const phase = PHASES.find((p) => p.code === code);
  return phase ? `${phase.code} · ${phase.libelle}` : code;
};

function useLargeur(): number {
  const lire = (): number => globalThis.innerWidth ?? 1280;
  const [largeur, setLargeur] = useState(lire);
  useEffect(() => {
    const mesurer = (): void => setLargeur(lire());
    mesurer();
    globalThis.addEventListener?.('resize', mesurer);
    return () => globalThis.removeEventListener?.('resize', mesurer);
  }, []);
  return largeur;
}

const champ: CSSProperties = {
  minHeight: CIBLE_MIN,
  boxSizing: 'border-box',
  padding: '0 12px',
  border: `1px solid ${COULEURS.bordure}`,
  borderRadius: 8,
  font: 'inherit',
  color: COULEURS.marineFonce,
  background: COULEURS.blanc,
};
const etiquette: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 14,
  color: COULEURS.marine,
  fontWeight: 600,
};
const carte: CSSProperties = { background: COULEURS.blanc, border: '1px solid #E2E1EA', borderRadius: 12, padding: 16 };
const lienDossier: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: CIBLE_MIN,
  color: COULEURS.marine,
  fontWeight: 600,
  textDecoration: 'underline',
};

function Statut({ code }: { code: string }) {
  const ton = code === 'bloque' ? 'bloque' : code === 'actif' ? 'bonne-voie' : 'neutre';
  return (
    <Pastille ton={ton} data-statut-dossier={code}>
      {libelleStatutDossier(code)}
    </Pastille>
  );
}

const pointAttention = (l: LigneDossier): string => (l.blocker ? presenterBlocage(l.blocker).libelle : 'Aucun blocage.');
const famille = (l: LigneDossier): string =>
  `${l.heirsCount} ${l.heirsCount > 1 ? 'héritiers' : 'héritier'} · ${l.respondedCount} ${l.respondedCount > 1 ? 'réponses' : 'réponse'}`;

function Tableau({ lignes }: { lignes: readonly LigneDossier[] }) {
  const th: CSSProperties = {
    textAlign: 'left',
    padding: '8px 12px',
    color: COULEURS.marine,
    fontSize: 14,
    borderBottom: `2px solid ${COULEURS.marine}`,
  };
  const td: CSSProperties = { padding: '4px 12px', verticalAlign: 'middle', borderBottom: '1px solid #E2E1EA' };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <caption style={{ position: 'absolute', left: -9999 }}>Dossiers</caption>
      <thead>
        <tr>
          {['Dossier', 'Étape', 'Statut', 'Famille', 'Point d’attention'].map((t) => (
            <th key={t} scope="col" style={th}>
              {t}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {lignes.map((l) => (
          <tr key={l.id}>
            <td style={td}>
              <Lien vers={`/dossier?dossier=${encodeURIComponent(l.id)}`} enfants={l.title} style={lienDossier} />
              <br />
              <span style={{ color: COULEURS.ardoise, fontSize: 14 }}>{l.reference}</span>
            </td>
            <td style={td}>{libellePhase(l.currentPhase)}</td>
            <td style={td}>
              <Statut code={l.status} />
            </td>
            <td style={td}>{famille(l)}</td>
            <td style={td}>{pointAttention(l)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Cartes({ lignes }: { lignes: readonly LigneDossier[] }) {
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {lignes.map((l) => (
        <li key={l.id} style={{ ...carte, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Lien vers={`/dossier?dossier=${encodeURIComponent(l.id)}`} enfants={l.title} style={lienDossier} />
          <span style={{ color: COULEURS.ardoise, fontSize: 14 }}>{l.reference}</span>
          <span>
            <Statut code={l.status} />
          </span>
          <span>{libellePhase(l.currentPhase)}</span>
          <span>{famille(l)}</span>
          <span>{pointAttention(l)}</span>
        </li>
      ))}
    </ul>
  );
}

function FormulaireNouveauDossier({ source, onAnnuler }: { source: SourceDossiers; onAnnuler: () => void }) {
  const naviguer = useNaviguer();
  const [titre, setTitre] = useState('');
  const [venuPar, setVenuPar] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const verrou = useRef(false);

  async function valider(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (verrou.current) return;
    if (!titre.trim()) {
      setErreur(MESSAGE_NOM_VIDE);
      return;
    }
    verrou.current = true;
    setEnCours(true);
    setErreur(null);
    try {
      const cree = await source.creer(titre, venuPar);
      naviguer(`/dossier?dossier=${encodeURIComponent(cree.id)}`);
    } catch (err) {
      setErreur(messageDe(err, MESSAGE_ECHEC_CREATION));
    } finally {
      verrou.current = false;
      setEnCours(false);
    }
  }

  return (
    <form
      data-formulaire="nouveau-dossier"
      onSubmit={(e) => void valider(e)}
      noValidate
      style={{ ...carte, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 640 }}
    >
      <h2 style={{ margin: 0, fontSize: 20, color: COULEURS.marine }}>Nouveau dossier</h2>
      <p style={{ margin: 0, color: COULEURS.ardoise }}>
        Le dossier démarre à l’étape {PHASES[0].code} ({PHASES[0].libelle.toLowerCase()}). Vous ferez ensuite signer le mandat de recherche
        depuis le dossier : tant qu’il n’est pas signé, aucune recherche d’héritier n’est possible.
      </p>
      <label htmlFor="nouveau-dossier-titre" style={etiquette}>
        Nom du dossier
        <input
          id="nouveau-dossier-titre"
          type="text"
          value={titre}
          autoComplete="off"
          aria-describedby="nouveau-dossier-aide"
          onChange={(e) => setTitre(e.target.value)}
          style={{ ...champ, fontWeight: 400 }}
        />
        <span id="nouveau-dossier-aide" style={{ fontWeight: 400, color: COULEURS.ardoise }}>
          Par exemple : Succession Dupont.
        </span>
      </label>
      {/* Décision de Joel (26/10) : question du premier contact, choix en donnée versionnée, facultative, vue par l'équipe seulement. */}
      <fieldset
        data-question="venu-par"
        style={{ border: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}
      >
        <legend style={{ ...etiquette, padding: 0, marginBottom: 4 }}>{PREMIER_CONTACT.question}</legend>
        {PREMIER_CONTACT.options.map((o) => (
          <label key={o.code} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: CIBLE_MIN, cursor: 'pointer' }}>
            <input
              type="radio"
              name="nouveau-dossier-venu-par"
              value={o.code}
              checked={venuPar === o.code}
              onChange={() => setVenuPar(o.code)}
              style={{ width: 22, minHeight: CIBLE_MIN, margin: 0 }}
            />
            {o.libelle}
          </label>
        ))}
      </fieldset>
      {erreur ? (
        <p role="alert" style={{ margin: 0, color: COULEURS.rouge }}>
          {erreur}
        </p>
      ) : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button type="submit" disabled={enCours} aria-disabled={enCours} style={{ ...styleBoutonPrincipal, minHeight: CIBLE_MIN }}>
          {enCours ? 'Création en cours…' : 'Créer le dossier'}
        </button>
        <button type="button" onClick={onAnnuler} style={{ ...styleBouton, minHeight: CIBLE_MIN }}>
          Annuler
        </button>
      </div>
    </form>
  );
}

type EtatListe =
  | { statut: 'chargement' }
  | { statut: 'erreur'; message: string }
  | { statut: 'pret'; lignes: LigneDossier[]; suite: string | null; chargementSuite: boolean };

export function ListeDossiers({ source }: { source: SourceDossiers }) {
  const largeur = useLargeur();
  const [etat, setEtat] = useState<EtatListe>({ statut: 'chargement' });
  const [formulaire, setFormulaire] = useState(() => new URLSearchParams(globalThis.location?.search ?? '').get('nouveau') === '1');
  const [recherche, setRecherche] = useState('');
  const [statut, setStatut] = useState('');

  const charger = useCallback(async () => {
    setEtat({ statut: 'chargement' });
    try {
      const page = await source.lister();
      setEtat({ statut: 'pret', lignes: page.items, suite: page.nextCursor, chargementSuite: false });
    } catch (e) {
      setEtat({ statut: 'erreur', message: messageDe(e, MESSAGE_ECHEC_LECTURE) });
    }
  }, [source]);
  useEffect(() => {
    void charger();
  }, [charger]);

  async function chargerSuite(curseur: string): Promise<void> {
    setEtat((e) => (e.statut === 'pret' ? { ...e, chargementSuite: true } : e));
    try {
      const page = await source.lister(curseur);
      setEtat((e) =>
        e.statut === 'pret' ? { statut: 'pret', lignes: [...e.lignes, ...page.items], suite: page.nextCursor, chargementSuite: false } : e,
      );
    } catch (err) {
      setEtat({ statut: 'erreur', message: messageDe(err, MESSAGE_ECHEC_LECTURE) });
    }
  }

  const lignes = etat.statut === 'pret' ? etat.lignes : [];
  const terme = recherche.trim().toLowerCase();
  const visibles = lignes.filter(
    (l) => (statut === '' || l.status === statut) && (terme === '' || `${l.title} ${l.reference}`.toLowerCase().includes(terme)),
  );

  return (
    <section
      data-ecran="D03"
      data-ecran-dossier="D03"
      data-composant="liste-dossiers"
      style={{ fontFamily: POLICE_EQUIPE, display: 'flex', flexDirection: 'column', gap: 16 }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, color: COULEURS.marine, fontSize: 28 }}>Dossiers</h1>
          {etat.statut === 'pret' ? (
            <p style={{ margin: '4px 0 0', color: COULEURS.ardoise }}>
              {lignes.length} {lignes.length > 1 ? 'dossiers' : 'dossier'} de succession ou d’indivision
            </p>
          ) : null}
        </div>
        <button type="button" onClick={() => setFormulaire(true)} style={{ ...styleBoutonPrincipal, minHeight: CIBLE_MIN }}>
          Nouveau dossier
        </button>
      </div>

      {formulaire ? <FormulaireNouveauDossier source={source} onAnnuler={() => setFormulaire(false)} /> : null}

      {etat.statut === 'chargement' ? <p role="status">Chargement des dossiers…</p> : null}
      {etat.statut === 'erreur' ? (
        <div role="alert" style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
          <p style={{ margin: 0, color: COULEURS.rouge }}>{etat.message}</p>
          <button type="button" style={{ ...styleBouton, minHeight: CIBLE_MIN }} onClick={() => void charger()}>
            Réessayer
          </button>
        </div>
      ) : null}

      {etat.statut === 'pret' && lignes.length === 0 ? (
        <div style={carte}>
          <p style={{ margin: 0 }}>Aucun dossier pour le moment. Créez le premier avec « Nouveau dossier ».</p>
        </div>
      ) : null}

      {etat.statut === 'pret' && lignes.length > 0 ? (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            <label htmlFor="filtre-dossiers-texte" style={{ ...etiquette, flex: '1 1 240px' }}>
              Rechercher un dossier
              <input
                id="filtre-dossiers-texte"
                type="search"
                value={recherche}
                placeholder="Nom ou référence"
                onChange={(e) => setRecherche(e.target.value)}
                style={{ ...champ, fontWeight: 400 }}
              />
            </label>
            <label htmlFor="filtre-dossiers-statut" style={{ ...etiquette, flex: '0 1 220px' }}>
              Statut
              <select
                id="filtre-dossiers-statut"
                value={statut}
                onChange={(e) => setStatut(e.target.value)}
                style={{ ...champ, fontWeight: 400 }}
              >
                <option value="">Tous les statuts</option>
                {REFERENTIEL_LISTE.statuts.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.libelle}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {visibles.length === 0 ? (
            <p role="status">Aucun dossier ne correspond à votre recherche.</p>
          ) : largeur >= LARGEUR_TABLEAU ? (
            <div style={{ ...carte, padding: 8, overflowX: 'auto' }}>
              <Tableau lignes={visibles} />
            </div>
          ) : (
            <Cartes lignes={visibles} />
          )}
          {etat.suite ? (
            <div>
              <button
                type="button"
                disabled={etat.chargementSuite}
                style={{ ...styleBouton, minHeight: CIBLE_MIN }}
                onClick={() => void chargerSuite(etat.suite as string)}
              >
                Afficher les dossiers suivants
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
